import React from 'react';
import './styles.css';
import { FormContainer, FormRowView, TableColumnContainer, TableItemTextView, TableItemView, TableView } from 'components/shared';
import { SVGAsset } from 'res';
import { DisabledPrompt, Link, useUIState } from "bcms-react";
import CustomTableAccordion, { CustomTableContent, CustomTableToggle, useCustomTableAccordion } from 'components/custom-ui/CustomTableAccordion';
import MobilePendingApprovalTable from '../MobilePendingApprovalTable';
import { TransferTransaction } from "model";

/**
 * @function PendingApprovalPage
 * @return {React.Component}
 * @exported true
 * @description The pending approvals tab in the TransferActivityPage
 */

export default function PendingApprovalPage(props){
    const transfers = props.transfers ?? TransferTransaction.initAll().filter(transfer => transfer.needsApproval, []);

  return(
    <div className="pending-approval-page">
        <div className="show-on-desktop">
            <PendingApprovalTable transfers={ transfers } />
        </div>
        <div className="show-on-mobile">
            <MobilePendingApprovalTable transfers={ transfers } />
        </div>
    </div>
  );
}

/**
 * @function PendingApprovalTable
 * @return {React.Component}
 * @exported false
 * @description The table that shows the pending approvals
 */

function PendingApprovalTable(props){
    const transfers = props.transfers ?? [];
    const transferRows = transfers.map(transfer => {
        return <PendingApprovalItem key={ `Transfer_Pending_Approval_${ transfer.id }`} transfer={ transfer } />
    }, []);

  return(
    <TableView className="pending-approval-table">
      <TableColumnContainer className="pending-approvals-header">
      <th>&nbsp;</th>
      <th className="text-left">
          From
          <DisabledPrompt>
              <SVGAsset id="SortIcon" className="sort-icon clickable" />
          </DisabledPrompt>
      </th>
      <th className="text-left">
          To
          <DisabledPrompt>
              <SVGAsset id="SortIcon" className="sort-icon clickable" />
          </DisabledPrompt>    
      </th>
      <th className="text-left">
          Schedule
          <DisabledPrompt>
              <SVGAsset id="SortIcon" className="sort-icon clickable" />
          </DisabledPrompt> 
      </th>
      <th className="text-left">
          Next Transfer
          <DisabledPrompt>
              <SVGAsset id="ArrowUpIcon" className="sort-icon clickable" />
          </DisabledPrompt> 
      </th>
      <th className="text-left">
          Approvals
          <DisabledPrompt>
              <SVGAsset id="SortIcon" className="sort-icon clickable" />
          </DisabledPrompt> 
      </th>
      <th className="text-right">
          Amount
          <DisabledPrompt>
              <SVGAsset id="SortIcon" className="sort-icon clickable" />
          </DisabledPrompt> 
      </th>
      </TableColumnContainer>

      { transferRows }
    </TableView>
  );
}

/**
 * @function PendingApprovalItem
 * @return {React.Component}
 * @exported false
 * @description The item in the PendingApprovalTable
 */

function PendingApprovalItem(props){
    const transfer = props.transfer ?? TransferTransaction.firstInstanceOf();
    const { key, config } = useUIState(`Accordion_${ transfer.id }`, { isActive: false });

  return(
    <CustomTableAccordion activeClass="open-custom-transaction-accordion" state={ config.isActive }>
      <PendingApprovalItemToggle transfer={ transfer } />
      <PendingApprovalItemContent transfer={ transfer } />
    </CustomTableAccordion>
  );
}

/**
 * @function PendingApprovalItemToggle
 * @return {React.Component}
 * @exported false
 * @description The Item toggle for PendingApprovalItem
 */

function PendingApprovalItemToggle(props){
    const transfer = props.transfer ?? TransferTransaction.firstInstanceOf();
    const { state } = useCustomTableAccordion();
    const schedule = TransferTransaction.getPendingApprovalTableScheduleMessage(transfer);
    //const scheduleType = transfer.schedule.toLowerCase();
    let nextTransfer = transfer.onDate.format("m/d/Y");

    // May be needed later with full cert support.
    /*if(scheduleType.includes("recurring")) {
        nextTransfer = transfer.startDate.format("m/d/Y");
    }*/

  return(
    <CustomTableToggle>
            <tr>
                <TableItemView className="expand-row-chevron text-center" data-point={ `Transfer_Pending_Approval_Arrow_${ transfer.id }` }>
                    <SVGAsset id={ state ? "DownChevronIcon" : "RightChevronIcon" } className="icon" />
                </TableItemView>

                <TableItemTextView className="text-left expanded" text={ transfer.from.nickname +" - " + transfer.from.number } />
                <TableItemTextView className="text-left expanded" text={ transfer.to.nickname +" - " + transfer.to.number } />
                <TableItemTextView className="text-left expanded" text={ schedule } />
                <TableItemTextView className="text-left expanded" text={ nextTransfer } />
                <TableItemView className="text-left expanded">
                  <div className="flex-container flex-horizontal align-center">
                    <SVGAsset id="ClockIcon" className="icon inline-text-icon left-icon" />
                    <p><span>{ transfer.numberOfApprovals }</span> of <span>{ transfer.numberOfApprovalsNeeded }</span></p>
                  </div>
                </TableItemView>
                <TableItemTextView className="text-right expanded amount-column" text={ transfer.amount.format() } />
            </tr>
        </CustomTableToggle>
  );
}

/**
 * @function PendingApprovalItemContent
 * @return {React.Component}
 * @exported false
 * @description description
 */

function PendingApprovalItemContent(props){
    const transfer = props.transfer ?? TransferTransaction.firstInstanceOf();
    const approveByDate = `${ transfer.approveByDate.format("m/d/Y") } 05:00 PM`;
    const schedule = TransferTransaction.getPendingApprovalTableDetailsScheduleMessage(transfer);
    //const scheduleType = transfer.schedule.toLowerCase();
    let nextTransfer = transfer.onDate.format("m/d/Y");

    // May be needed later with full cert support.
    /*if(scheduleType.includes("recurring")) {
        nextTransfer = transfer.startDate.format("m/d/Y");
    }*/

  return(
    <CustomTableContent>
      <tr>
          <td className="pending-approval-table-item" colSpan="7">
              <div className="flex-container">
                  <p className="flex-width">TRANSFER DETAILS</p>         
                  <CustomTableToggle>
                      <SVGAsset id="CloseX" className="set-width clickable" />
                  </CustomTableToggle>          
              </div>

              <FormContainer>
                  <FormRowView text="Type">
                      <p>Internal Transfer</p>
                  </FormRowView>
                  
                  <FormRowView text="Deliver By">
                      <p>{ nextTransfer }</p>
                  </FormRowView>
                  
                  <FormRowView text="Schedule">
                      <p>{ schedule }</p>
                  </FormRowView>
                  
                  <FormRowView text="Note">
                      <p>{ transfer.note }</p>
                  </FormRowView>

                  <FormRowView text="Approve By">
                      <p>{ approveByDate }</p>
                  </FormRowView>

                  <FormRowView text="Created By">
                      <p>{ transfer.createdBy.fullName }</p>
                  </FormRowView>
                  
                  <FormRowView className="link-container">
                      <DisabledPrompt>
                          <Link>
                              Edit Transfer
                          </Link>
                      </DisabledPrompt>
                  </FormRowView>
                  
                  <FormRowView className="link-container">
                      <DisabledPrompt>
                          <Link>
                              Cancel Transfer
                          </Link>
                      </DisabledPrompt>
                  </FormRowView>
              </FormContainer>
          </td>
      </tr>
  </CustomTableContent>
  );
}